import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, Dialog, DialogContent,
  DialogTitle, IconButton, Snackbar, Stack, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { usePageTitle } from '../../components/shell/AppShell';
import {
  useCashDeskLayout, useMachinePhoto, useSaveCashDeskLayout, useUploadMachinePhoto,
} from '../../api/queries';
import type { CashDeskLayoutRow } from '../../api/queries';
import { SitePicker } from './SitePicker';

/**
 * Direction-A floor layout editor (plan Draft 1 W8a, per the MapSetup
 * mockup): drag machine tiles to mirror the real floor — line, dog-leg,
 * circle, anything. Placement order sets the position number the cashier
 * sees; photos (PNG/JPEG/BMP ≤ 512 KB) make machines recognisable on the
 * till — the canvas tiles render them exactly like the till does, and
 * clicking a thumbnail shows the full image. Save replaces the whole plan;
 * it reaches the site controller on its next config sync.
 *
 * The editor is site-scoped (`FloorLayoutEditor`) so the Sites page's
 * tabbed detail view embeds it directly; this page wraps it in a picker.
 */

type Placed = { machineId: string; positionNo: number; x: number; y: number; rotation: number };

const GRID = 10;
const TILE_W = 96;
const TILE_H = 92;

export default function FloorLayoutPage() {
  usePageTitle('Cash desk floor layout');
  const [siteId, setSiteId] = useState<string | null>(null);
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Cash desk floor layout</Typography>
      <SitePicker siteId={siteId} onChange={setSiteId} />
      {siteId && <FloorLayoutEditor siteId={siteId} />}
    </Stack>
  );
}

export function FloorLayoutEditor({ siteId }: { siteId: string }) {
  const layout = useCashDeskLayout(siteId);
  const save = useSaveCashDeskLayout(siteId);
  const upload = useUploadMachinePhoto(siteId);

  const [placed, setPlaced] = useState<Placed[]>([]);
  const [dirty, setDirty] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CashDeskLayoutRow | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ machineId: string; dx: number; dy: number; moved: boolean } | null>(null);

  useEffect(() => {
    if (layout.data) {
      setPlaced(layout.data
        .filter((r): r is CashDeskLayoutRow & { positionNo: number; x: number; y: number } =>
          r.positionNo !== null && r.x !== null && r.y !== null)
        .map((r) => ({ machineId: r.machineId, positionNo: r.positionNo, x: r.x, y: r.y,
                       rotation: r.rotation ?? 0 })));
      setDirty(false);
    }
  }, [layout.data]);

  const byId = useMemo(() => new Map((layout.data ?? []).map((r) => [r.machineId, r])), [layout.data]);

  const place = (machineId: string) => {
    setPlaced((p) => [...p, {
      machineId,
      positionNo: p.length + 1,
      x: 40 + (p.length % 5) * (TILE_W + 24),
      y: 40 + Math.floor(p.length / 5) * (TILE_H + 44),
      rotation: 0,
    }]);
    setDirty(true);
  };

  const unplace = (machineId: string) => {
    setPlaced((p) => p.filter((e) => e.machineId !== machineId)
      .map((e, i) => ({ ...e, positionNo: i + 1 })));
    setDirty(true);
  };

  // Owner amendment 2026-08-23: reorder position numbers from the list —
  // swap with the neighbour and renumber 1..n by the new order.
  const move = (machineId: string, dir: -1 | 1) => {
    setPlaced((p) => {
      const ordered = [...p].sort((a, b) => a.positionNo - b.positionNo);
      const idx = ordered.findIndex((e) => e.machineId === machineId);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= ordered.length) return p;
      [ordered[idx], ordered[swap]] = [ordered[swap], ordered[idx]];
      return ordered.map((e, i) => ({ ...e, positionNo: i + 1 }));
    });
    setDirty(true);
  };

  const onPointerDown = (e: ReactPointerEvent, machineId: string) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const entry = placed.find((p) => p.machineId === machineId)!;
    drag.current = { machineId, dx: e.clientX - rect.left - entry.x, dy: e.clientY - rect.top - entry.y, moved: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const { machineId, dx, dy } = drag.current;
    const x = Math.round(Math.max(0, Math.min(rect.width - TILE_W, e.clientX - rect.left - dx)) / GRID) * GRID;
    const y = Math.round(Math.max(0, Math.min(rect.height - TILE_H, e.clientY - rect.top - dy)) / GRID) * GRID;
    drag.current.moved = true;
    setPlaced((p) => p.map((en) => (en.machineId === machineId ? { ...en, x, y } : en)));
    setDirty(true);
  };

  const onPointerUp = () => { drag.current = null; };

  const uploadPhoto = (machineId: string, file: File) => {
    setUploadError(null);
    if (file.size > 512 * 1024) {
      setUploadError(`Photo is ${Math.round(file.size / 1024)} KB — the limit is 512 KB. Downscale it and retry.`);
      return;
    }
    upload.mutate({ machineId, file }, {
      onError: (e) => setUploadError((e as { message?: string })?.message ?? 'Photo upload failed.'),
    });
  };

  if (!layout.data) return null;

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card sx={{ width: 360, flexShrink: 0 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Machines at this site
            </Typography>
            {uploadError && <Alert severity="error" sx={{ my: 1 }}>{uploadError}</Alert>}
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(layout.data ?? []).map((m) => {
                const isPlaced = placed.some((p) => p.machineId === m.machineId);
                return (
                  <Card key={m.machineId} variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MachineThumb machine={m} size={40}
                          onClick={() => m.hasPhoto && setPreview(m)} />
                        <Stack sx={{ minWidth: 0, flex: 1 }}>
                          {/* Full game name — wraps rather than truncating
                              (owner 2026-08-23). */}
                          <Typography variant="body2">{m.machineName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.cabinet ? `${m.cabinet} · ` : ''}
                            {isPlaced
                              ? `Position ${placed.find((p) => p.machineId === m.machineId)!.positionNo}`
                              : 'Not placed'}
                            {' · '}{m.hasPhoto ? 'photo set' : 'no photo'}
                          </Typography>
                        </Stack>
                        {isPlaced && (
                          <Stack spacing={0}>
                            <IconButton size="small" data-testid={`up-${m.assetNumber}`}
                              sx={{ p: 0.25 }}
                              onClick={() => move(m.machineId, -1)}>
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton size="small" data-testid={`down-${m.assetNumber}`}
                              sx={{ p: 0.25 }}
                              onClick={() => move(m.machineId, 1)}>
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        )}
                        <Button size="small" component="label" data-testid={`photo-${m.assetNumber}`}>
                          Photo
                          <input hidden type="file" accept="image/png,image/jpeg,image/bmp"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadPhoto(m.machineId, f);
                              e.target.value = '';
                            }} />
                        </Button>
                        {isPlaced ? (
                          <Button size="small" color="inherit"
                            onClick={() => unplace(m.machineId)}>Remove</Button>
                        ) : (
                          <Button size="small" variant="outlined"
                            data-testid={`place-${m.assetNumber}`}
                            onClick={() => place(m.machineId)}>Place</Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {(layout.data ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No machines at this site yet.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Drag machines to mirror the real floor — line, dog-leg, circle, anything.
            Tiles show the machine exactly as the cashier sees it on the till;
            positions snap to a {GRID}px grid. Click a photo to view it full size.
          </Typography>
          <Box
            ref={canvasRef}
            data-testid="floor-canvas"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            sx={{
              position: 'relative', height: 520, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              backgroundImage: 'radial-gradient(rgba(120,130,150,0.35) 1px, transparent 1px)',
              backgroundSize: '26px 26px', overflow: 'hidden', touchAction: 'none',
            }}
          >
            {placed.map((p) => {
              const m = byId.get(p.machineId);
              return (
                <Box key={p.machineId}
                  data-testid={`tile-${m?.assetNumber}`}
                  onPointerDown={(e) => onPointerDown(e, p.machineId)}
                  sx={{
                    position: 'absolute', left: p.x, top: p.y, width: TILE_W, height: TILE_H,
                    borderRadius: 1.5, border: '1px solid', borderColor: 'primary.main',
                    bgcolor: 'background.paper', boxShadow: 1, cursor: 'grab',
                    p: 0.75, userSelect: 'none', overflow: 'hidden',
                  }}>
                  {m && (
                    <MachineThumb machine={m} size={TILE_W - 12} height={40} rounded
                      onClick={() => { if (m.hasPhoto && !drag.current?.moved) setPreview(m); }} />
                  )}
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                    <Chip size="small" label={p.positionNo} color="primary"
                      sx={{ height: 18, fontSize: 11 }} />
                    <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
                      {m?.machineName}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>

          {(save.isError) && (
            <Alert severity="error">
              {(save.error as { message?: string })?.message ?? 'Save failed.'}
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Applies on the site controller's next config sync.
            </Typography>
            <Button variant="contained" disabled={!dirty || save.isPending}
              data-testid="layout-save"
              onClick={() => save.mutate(placed, { onSuccess: () => { setSavedToast(true); setDirty(false); } })}>
              Save layout
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="md">
        <DialogTitle>{preview?.machineName}</DialogTitle>
        <DialogContent>
          {preview && <FullPhoto machine={preview} />}
        </DialogContent>
      </Dialog>

      <Snackbar open={savedToast} autoHideDuration={4000}
        onClose={() => setSavedToast(false)} message="Floor layout saved" />
    </>
  );
}

/** The machine's photo the way the till renders it (cover-cropped);
 *  a lettered avatar when no photo is set. */
function MachineThumb({ machine, size, height, rounded, onClick }: {
  machine: CashDeskLayoutRow; size: number; height?: number; rounded?: boolean;
  onClick?: () => void;
}) {
  const photo = useMachinePhoto(
    machine.hasPhoto ? machine.machineId : null, machine.photoSha256 ?? null);
  if (!machine.hasPhoto || !photo.data) {
    return (
      <Avatar variant={rounded ? 'rounded' : 'circular'}
        sx={{ width: size, height: height ?? size, fontSize: 13 }}>
        {(machine.machineName ?? '?').slice(0, 2)}
      </Avatar>
    );
  }
  return (
    <Box component="img" src={photo.data} alt={machine.machineName ?? ''}
      data-testid={`thumb-${machine.assetNumber}`}
      onClick={onClick}
      sx={{
        width: size, height: height ?? size, objectFit: 'cover',
        borderRadius: rounded ? 1 : '50%', display: 'block',
        cursor: onClick ? 'zoom-in' : 'default', flexShrink: 0,
      }} />
  );
}

function FullPhoto({ machine }: { machine: CashDeskLayoutRow }) {
  const photo = useMachinePhoto(machine.machineId, machine.photoSha256 ?? null);
  if (!photo.data) return <Typography color="text.secondary">Loading photo…</Typography>;
  return (
    <Box component="img" src={photo.data} alt={machine.machineName ?? ''}
      data-testid="photo-preview"
      sx={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', borderRadius: 1 }} />
  );
}
